import { useAtomValue } from 'jotai'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { extensionsListAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export interface ConferenceRoomSelectorProps {
  /** Current selected conference room ID */
  value?: string
  /** Callback when conference room selection changes */
  onChange: (conferenceRoomId: string | undefined) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable conference room selector dropdown component.
 * Automatically filters to only show Conference Room extensions (ExtensionConferenceRoom type).
 *
 * Data Source: ExtensionConferenceRoom types from extensionsListAtom (Jotai atom)
 * Fetched via: GET /app/v1/extensions/?pbx_id={id}&type=ExtensionConferenceRoom
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/conference-room.md
 * Component: ConferenceRoomNode uses ConferenceRoomSelector
 */
export const ConferenceRoomSelector: React.FC<ConferenceRoomSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(
      t,
      'switchboard.core.selectConferenceRoom',
      'Select conference room...',
    )
  const label =
    labelProp ??
    text(t, 'switchboard.core.conferenceRoomLabel', 'Conference Room')
  const extensionsList = useAtomValue(extensionsListAtom)
  const availableConferenceRooms = React.useMemo(() => {
    if (!extensionsList) return []
    return extensionsList.filter((ext) => {
      if (ext.type !== 'ExtensionConferenceRoom') return false
      const extWithActive = ext as ExtensionInstance & { active?: boolean }
      if ('active' in extWithActive) {
        return extWithActive.active === true
      }
      return true
    })
  }, [extensionsList])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value || undefined)
    },
    [onChange],
  )

  return (
    <NodeSelect label={label} value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {availableConferenceRooms.map((room) => (
        <option key={room.id} value={String(room.id)}>
          {room.number} -{' '}
          {room.name || text(t, 'switchboard.core.unnamed', 'Unnamed')}
        </option>
      ))}
    </NodeSelect>
  )
}
